// API service for making HTTP requests
export interface HealthResponse {
  status: string;
  timestamp: string;
  service: string;
}

export interface ApiError {
  message: string;
  status?: number;
}

export interface RequestOptions extends RequestInit {
  headers?: HeadersInit;
}

export interface QuickInvoiceRequest {
  billToName: string;
  invoiceNumber: string;
  invoiceDate: string;
  projectDescription: string;
  hoursWorked: number;
}

export interface SaveQuickInvoiceRequest {
  invoiceNumber: string;
  clientId: string;
  clientName: string;
  projectName: string;
  hoursWorked: number;
  startDate: string | null; // ISO date string
  endDate: string | null;   // ISO date string
  description: string;
  deliverables: string;
}

export interface QuickInvoiceResponse {
  success: boolean;
  invoiceId?: string;
  message?: string;
  data?: any;
}

export interface WorkflowRequest {
  workFlowData: string;
  requestType?: string | null;
  threadId?: string | null;
}

export interface WorkflowResponse {
  message: string;
  data: Array<{
    clientName: string | null;
    clientKnown: boolean | false;
    clientId: string | null; 
    projectName: string | null;
    hoursWorked: number | null;
    startDate: string | null;
    endDate: string | null;
    description: string | null;
    dataRequest: string | null;
    question: string | null;
    threadId: string | null;
    answer: string | null;
    requestType?: string | null;
    invoiceNumber: string | null;
    deliverables: string | null;
    clients: string[];
  }>;
}

export interface CreateLogRequest {
    clientName: string | null;
    clientKnown: boolean | false;
    clientId: string | null; 
    projectName: string | null;
    hoursWorked: number | null;
    startDate: string | null;
    endDate: string | null;
    description: string | null;
    dataRequest: string | null;
    question: string | null;
    answer: string | null;
    requestType?: string | null;
    invoiceNumber: string | null;
    deliverables: string | null;
    clients: string[];
}

export interface ProjectRequest {
  id?: string | null;
  clientId?: string | null;
  clientName?: string | null;
  projectName?: string | null;
  projectUsers?: string[] | null;
  projectType?: string | null;
  hourlyRate?: number | null;
  budget?: number | null;
  isActive: boolean;  
  projectStatus?: string | null;
  notes?: string | null;
  projectDocuments?: string[] | null;
  projectLinks?: string[] | null;
  createdOn?: string | null; // ISO date string (DateTimeOffset)
}

export interface CreateLogResponse {
  success: boolean;
  message?: string;
  logId?: string;
}

export interface PayoutSummary {
  startDate: string;
  endDate: string;
  amountPaid: number;
  hoursPaid: number;
  invoicesPaid: number;
  amountUnPaid: number;
  hoursUnPaid: number;
  invoicesUnpaid: number;
  invoicesRejected: number;
}

export interface PayoutRequest {
  hoursPendingPayment: number;
  amountPendingPayment: Date;
}

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5147') {
    this.baseUrl = baseUrl;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorMessage = `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    
    // If response is not JSON, return the text as the result
    const text = await response.text();
    return { status: 'success', data: text } as unknown as T;
  }

  async healthCheck(): Promise<HealthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return this.handleResponse<HealthResponse>(response);
    } catch (error) {
      console.error('Health check failed:', error);
      throw {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 0,
      } as ApiError;
    }
  }

  async publicRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error(`Public API request to ${endpoint} failed:`, error);
      throw {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 0,
      } as ApiError;
    }
  }

  async authenticatedRequest<T>(endpoint: string, accessToken: string, options: RequestInit = {}): Promise<T> {
    try {
      console.log(`Making authenticated request to ${this.baseUrl}${endpoint}`);
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          ...options.headers,
        },
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error(`API request to ${endpoint} failed:`, error);
      throw {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 0,
      } as ApiError;
    }
  }

  async createQuickInvoice(invoiceData: QuickInvoiceRequest, accessToken: string): Promise<QuickInvoiceResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/Invoice/quickInvoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(invoiceData),
      });

      if (!response.ok) {
        const errorMessage = `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      // Check if the response is a PDF
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/pdf')) {
        // Handle PDF download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice-${invoiceData.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return {
          success: true,
          message: 'Invoice downloaded successfully',
          invoiceId: invoiceData.invoiceNumber,
        };
      }

      // If not PDF, handle as JSON response
      return this.handleResponse<QuickInvoiceResponse>(response);
    } catch (error) {
      console.error('Quick invoice creation failed:', error);
      throw {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 0,
      } as ApiError;
    }
  }

  async saveQuickInvoice(
    invoiceData: SaveQuickInvoiceRequest,
    accessToken: string
  ): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/Invoice/CreateFromScratch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(invoiceData),
      });

      this.handleResponse(response);
      return true
    } catch (error) {
      console.error('Save quick invoice failed:', error);
      throw {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 0,
      } as ApiError;
    }
  }

  async startWorkflow(workflowData: WorkflowRequest): Promise<WorkflowResponse> {
    console.log('Starting workflow with data:', workflowData);
    try {
      const response = await fetch(`${this.baseUrl}/api/start/talking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workflowData),
      });

      return this.handleResponse<WorkflowResponse>(response);
    } catch (error) {
      console.error('Workflow start failed:', error);
      throw {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 0,
      } as ApiError;
    }
  }

  async createLog(logData: CreateLogRequest, accessToken: string): Promise<CreateLogResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/Log/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(logData),
      });

      return this.handleResponse<CreateLogResponse>(response);
    } catch (error) {
      console.error('Log creation failed:', error);
      throw {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 0,
      } as ApiError;
    }
  }

  payoutRequest = async (accessToken: string) : Promise<PayoutRequest> => {
    try {
      const response = await fetch(`${this.baseUrl}/api/Payout/Request`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      return this.handleResponse<PayoutRequest>(response);
    } catch (error) {
      console.error('Payout request failed:', error);
      throw {
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        status: 0,
      } as ApiError;
    }
  }

  payoutSummary = async (accessToken: string) : Promise<PayoutSummary> => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(endDate.getMonth() - 3);

    const response = await fetch(`${this.baseUrl}/api/Payout/Summary`, { 
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        "startDate": startDate.toISOString(),
        "endDate": endDate.toISOString()
      })  
    });
    if (!response.ok) throw new Error('Network response was not ok');
    return this.handleResponse<PayoutSummary>(response);
  };
}

export const apiService = new ApiService();
export default apiService;
